/**
 * switchOrg — clears all scope state, re-authenticates to the new org,
 * re-fetches org data, then delegates to switchWorkspace for the default workspace.
 */

const ORG_KEYS = new Set(['org', 'permissions', 'orgMembers', 'orgRoles', 'orgTeams'])
const WORKSPACE_KEYS = new Set(['workspace', 'workspaceMembers', 'workspaceIntegrations', 'workspaceBilling'])
const WORKSPACE_PROJECT_KEYS = new Set(['workspaceProject'])
const PROJECT_KEYS = new Set(['project', 'projectComponents', 'projectPages', 'projectState'])

/**
 * @param {object} state        - DOMQL root state
 * @param {object} sdk          - SDK instance (must have switchOrg + organization + workspace)
 * @param {object} queryClient  - @symbo.ls/fetch queryClient
 * @param {string} newOrgId     - target organisation ID
 */
export const switchOrg = async (state, sdk, queryClient, newOrgId) => {
  if (!sdk) {
    // Without an SDK ref the rest is a fake switch — clearing state would
    // race with the auth subscriber and look like a partial success. Bail
    // loudly so the caller's wiring bug surfaces in the console.
    // eslint-disable-next-line no-console
    console.warn('[switchOrg] aborted — SDK reference missing on element context')
    return
  }
  for (const k of WORKSPACE_KEYS) state[k] = null
  for (const k of WORKSPACE_PROJECT_KEYS) state[k] = null
  for (const k of PROJECT_KEYS) state[k] = null
  for (const k of ORG_KEYS) state[k] = null

  await sdk.switchOrg(newOrgId)
  // queryClient is optional — shells that haven't wired @symbo.ls/fetch
  // yet (workspace shell on the `next` branch) won't have one.
  if (queryClient && typeof queryClient.clear === 'function') queryClient.clear()

  // SDK methods are flat — sdk.organization / sdk.auth / sdk.workspace
  // namespaces don't exist. Service routing is via the SERVICE_METHODS map
  // in sdk/src/utils/services.js, which exposes:
  //   sdk.getOrganization(id)         (organization service)
  //   sdk.getPermissions(...)         (auth service — only via dispatcher)
  //   sdk.listWorkspaces({ org })     (workspace service)
  // Permissions endpoint isn't shipped on every backend yet — failure
  // there shouldn't abort the switch (we only need org + workspaces to
  // bring the UI up). Fall back to null and let the auth subscriber's
  // jwt-claims-derived role gates run instead.
  const [org, permissions] = await Promise.all([
    sdk.getOrganization(newOrgId),
    Promise.resolve(
      typeof sdk.getPermissions === 'function'
        ? sdk.getPermissions(newOrgId)
        : sdk.execute?.('auth.permissions', 'list', [newOrgId])
    ).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[switchOrg] permissions fetch failed (continuing):', err?.message || err)
      return null
    })
  ])
  state.org = org
  state.permissions = permissions

  const workspaces = await sdk
    .listWorkspaces({ organization: newOrgId })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[switchOrg] listWorkspaces failed:', err?.message || err)
      return []
    })
  state.workspaces = Array.isArray(workspaces) ? workspaces : (workspaces?.data || [])
  const defaultWorkspace = state.workspaces[0] || null
  if (defaultWorkspace) {
    const { switchWorkspace } = await import('./switchWorkspace.js')
    await switchWorkspace(state, sdk, queryClient, defaultWorkspace.id || defaultWorkspace._id)
  }

  localStorage.setItem('activeOrg', newOrgId)
}
